import { type ReactNode } from 'react';
import { AlertTriangle, CloudOff } from 'lucide-react';
import { Card } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import type { WidgetState } from '../../widget-state';

export function widgetStateCopy(state: WidgetState): { title: string; body: string } {
  switch (state) {
    case 'loading': return { title: 'Loading', body: '' };
    case 'empty': return { title: 'Nothing here yet', body: '' };
    case 'error': return { title: 'Could not load', body: 'The service did not answer. Retry, or report it if it persists.' };
    case 'unavailable': return { title: 'Not connected', body: 'No live source for this number yet.' };
    default: return { title: '', body: '' };
  }
}

export interface WidgetShellProps {
  title: string;
  state: WidgetState;
  /** Only where it encodes something real — a period, a scope, a source system. */
  eyebrow?: string;
  /** For `empty`: what the user can do to make the tile non-empty. */
  action?: ReactNode;
  /** For `unavailable`: what is missing, including the endpoint it would read. */
  reason?: string;
  /** The server's own reason, shown verbatim under the generic error line. */
  detail?: string;
  onRetry?: () => void;
  children?: ReactNode;
}

/** The one card chrome every dashboard tile shares: hairline border, 12px
 *  radius, one lift, an 18px content rhythm and a hairline header rule. */
const CARD_CLASS = 'flex h-full flex-col overflow-hidden bg-card shadow-xs';

function ShellHeader({ title, eyebrow }: { title: string; eyebrow?: string }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
      <div className="min-w-0">
        {eyebrow ? (
          <div className="truncate text-label-caps uppercase tracking-wider text-on-surface-subtle">{eyebrow}</div>
        ) : null}
        <h3 className="truncate text-body-sm font-semibold text-on-surface">{title}</h3>
      </div>
    </div>
  );
}

/**
 * One place decides how every dashboard tile looks in every state, so a missing
 * endpoint can never be mistaken for a real zero.
 */
export function WidgetShell({ title, state, eyebrow, action, reason, detail, onRetry, children }: WidgetShellProps) {
  const copy = widgetStateCopy(state);

  if (state === 'loading') {
    return (
      <Card className={CARD_CLASS}>
        <ShellHeader title={title} eyebrow={eyebrow} />
        <div className="flex-1 space-y-2.5 p-4" role="status" aria-busy="true" aria-live="polite">
          <span className="sr-only">Loading {title}</span>
          <div className="h-3 w-1/3 animate-pulse rounded bg-surface-container" />
          <div className="h-7 w-1/2 animate-pulse rounded bg-surface-container" />
          <div className="h-3 w-2/3 animate-pulse rounded bg-surface-container" />
          <div className="h-3 w-1/2 animate-pulse rounded bg-surface-container" />
        </div>
      </Card>
    );
  }

  if (state === 'unavailable') {
    return (
      <Card className={`${CARD_CLASS} border-dashed`}>
        <ShellHeader title={title} eyebrow={eyebrow} />
        <div className="flex-1 space-y-1.5 p-4">
          <div className="flex items-start gap-2 text-body-sm text-on-surface-variant">
            <CloudOff className="mt-0.5 h-4 w-4 shrink-0 text-on-surface-subtle" aria-hidden />
            <span>{copy.body}</span>
          </div>
          {reason ? <p className="break-words font-mono text-data-xs text-on-surface-subtle">{reason}</p> : null}
        </div>
      </Card>
    );
  }

  if (state === 'error') {
    return (
      <Card className={CARD_CLASS}>
        <ShellHeader title={title} eyebrow={eyebrow} />
        <div className="flex flex-1 flex-col gap-2 p-4">
          <p className="flex items-start gap-2 text-body-sm text-on-surface-variant">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-danger-text" aria-hidden />
            <span>{copy.body}</span>
          </p>
          {detail ? <p className="break-words border-l-2 border-border pl-2 font-mono text-data-xs text-on-surface-subtle">{detail}</p> : null}
          {onRetry ? (
            <div className="mt-auto">
              <Button variant="outline" size="sm" onClick={onRetry}>Retry</Button>
            </div>
          ) : null}
        </div>
      </Card>
    );
  }

  return (
    <Card className={CARD_CLASS}>
      <ShellHeader title={title} eyebrow={eyebrow} />
      <div className="flex-1 p-4">
        {state === 'empty' ? (
          <div className="flex h-full flex-col items-start justify-center gap-2.5 py-2">
            <p className="text-body-sm text-on-surface-variant">
              {copy.title}. {reason || 'Nothing has been recorded against this yet.'}
            </p>
            {action}
          </div>
        ) : children}
      </div>
    </Card>
  );
}
